import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  makeStyles,
  tokens,
  Text,
  Spinner,
  Badge,
} from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import { format, startOfWeek, addWeeks } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useReservations, useDeleteReservation } from '../../hooks/useReservations';
import { useAuth } from '../../hooks/useAuth';
import { useDateLocale } from '../../hooks/useDateLocale';
import type { Reservation } from '@cp-parkingslots/shared';

const useStyles = makeStyles({
  surface: {
    maxWidth: '500px',
    width: '100%',
    '@media (max-width: 599px)': {
      maxWidth: '100%',
      margin: tokens.spacingHorizontalS,
      maxHeight: '90vh',
    },
  },
  dialogContent: {
    '@media (max-width: 599px)': {
      maxHeight: '60vh',
      overflowY: 'auto',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  reservationItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    gap: tokens.spacingHorizontalS,
    '@media (max-width: 599px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: tokens.spacingVerticalS,
    },
  },
  reservationInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
    minWidth: 0,
  },
  dateTime: {
    fontWeight: tokens.fontWeightSemibold,
    '@media (max-width: 599px)': {
      fontSize: tokens.fontSizeBase300,
    },
  },
  slot: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  empty: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXL,
    color: tokens.colorNeutralForeground2,
  },
  recurringBadge: {
    marginLeft: tokens.spacingHorizontalS,
    '@media (max-width: 599px)': {
      display: 'block',
      marginLeft: 0,
      marginTop: tokens.spacingVerticalXS,
    },
  },
  deleteButton: {
    flexShrink: 0,
    '@media (max-width: 599px)': {
      width: '100%',
    },
  },
  actions: {
    '@media (max-width: 599px)': {
      '& button': {
        width: '100%',
      },
    },
  },
});

interface MyReservationsDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function MyReservationsDialog({
  open,
  onClose,
}: MyReservationsDialogProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { user } = useAuth();
  const deleteReservation = useDeleteReservation();

  // Get reservations for next 4 weeks
  const today = new Date();
  const startDate = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const endDate = format(addWeeks(today, 4), 'yyyy-MM-dd');

  const { data: reservations, isLoading } = useReservations(startDate, endDate);

  const myReservations = reservations
    ?.filter((r) => r.userId === user?.id)
    .sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.startHour - b.startHour;
    });

  const handleDelete = async (reservation: Reservation) => {
    if (confirm(t('myReservations.confirmDelete', { date: format(new Date(reservation.date), 'd.M.yyyy') }))) {
      await deleteReservation.mutateAsync(reservation.id);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>{t('myReservations.title')}</DialogTitle>
          <DialogContent className={styles.dialogContent}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: tokens.spacingVerticalXL }}>
                <Spinner label={t('myReservations.loading')} />
              </div>
            ) : !myReservations?.length ? (
              <div className={styles.empty}>
                <Text>{t('myReservations.empty')}</Text>
              </div>
            ) : (
              <div className={styles.content}>
                {myReservations.map((reservation) => (
                  <div key={reservation.id} className={styles.reservationItem}>
                    <div className={styles.reservationInfo}>
                      <Text className={styles.dateTime}>
                        {format(new Date(reservation.date), 'EEE, d. MMM', {
                          locale: dateLocale,
                        })}
                        {' • '}
                        {reservation.startHour}:00 - {reservation.endHour}:00
                        {reservation.recurringId && (
                          <Badge
                            className={styles.recurringBadge}
                            appearance="outline"
                            size="small"
                          >
                            {t('myReservations.recurring')}
                          </Badge>
                        )}
                      </Text>
                      <Text className={styles.slot}>
                        {t(`slots.${reservation.slotId}` as const)}
                      </Text>
                    </div>
                    <Button
                      className={styles.deleteButton}
                      appearance="subtle"
                      icon={<DeleteRegular />}
                      onClick={() => handleDelete(reservation)}
                      disabled={deleteReservation.isPending}
                    >
                      {t('reservation.delete')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button appearance="secondary" onClick={onClose}>
              {t('reservation.close')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
